import React, { useContext } from 'react';
import Styled from 'styled-components';
import { useRouter } from 'next/router';
import Image, { ImageProps } from 'next/image';
import Link from 'next/link';
import { ApolloClient, InMemoryCache } from '@apollo/client';
import { Trans } from '@lingui/react';
import format from 'date-fns/format';
import addMinutes from 'date-fns/addMinutes';
import { sv, nb, fi, enGB } from 'date-fns/locale';
import { t } from '@lingui/macro';
import {
  LiveEventDocument,
  LiveEventQuery,
  useLiveEventQuery,
} from '@gql/generated';
import { Container, Flex, Box } from '@components/layout/Grid';
import LightningIcon from '@assets/LightingIcon';
import PulseIcon from '@assets/PulseIcon';
import ClockIcon from '@assets/ClockIcon';
import CalendarIcon from '@assets/CalendarIcon';
import ArrowRightIcon from '@assets/ArrowRightIcon';
import InfoIcon from '@assets/InfoOutlineIcon';
import ReserveBanner from '@ecosystems/events/ReserveBanner';
import EventDetails from '@ecosystems/events/EventDetails';
import InstructorModal from '@organisms/modals/InstructorModal';
import Spinner from '@components/Spinner';
import EventZoomDetails from '@ecosystems/events/EventZoomDetails';
import EventStickyBanner from '@ecosystems/events/EventStickyBanner';
import { UserContext } from '@lib/contexts/UserProvider';
import MetaTags from '@components/MetaTags';
import redirect from '@lib/redirect';
import EventRecordingRow from '@ecosystems/events/EventRecordingRow';
import { LiveEvent } from '@gql/generated';
import PricingSection from '@ecosystems/pricing/PricingSection';
import { LIVE_KINDS } from '@lib/constants';
import Banner from '@organisms/Banner';
import { Button } from '@components/ButtonV2';

const AllEventsLink = Styled(Button)`

  svg {
    margin-right: 12px;
    transform: rotateY(180deg);
  }
`;

const Content = Styled.div`
  max-width: 850px;
  width: 100%;
  margin: 20px auto; 
  padding: 0 20px;
`;

const Tag = Styled.span<{
  size?: number;
  outline?: boolean;
  plain?: boolean;
  full?: boolean;
}>`
  display: flex;
  align-items: center;
  font-size: ${({ plain }) => (plain ? 14 : 11)}px;
  color: ${({ full }) => (full ? 'white' : '#1c343e')};
  font-weight: 600;
  line-height: 1em;
  ${({ plain }) => (plain ? '' : 'border: 1px solid #1c343e;')}
  border-radius: 3px;
  padding: ${({ plain }) => (plain ? '6px 6px 6px 0' : '6px')};
  margin-right: 8px;
  background-color: ${({ full }) => (full ? '#1c343e' : 'transparent')};

  svg {
    margin-right: 6px;
  }

  :empty {
    display: none;
  }
`;

const LanguageTag = Styled(Tag)`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #1c343e;
  font-weight: 600;
  line-height: 1em;
  margin-left: 10px;
`;

const SectionTitle = Styled.p`
  font-size: 18px;
  font-weight: 300;
  margin: 0 0 20px 0;
`;

const EventDescription = Styled.div`
  font-size: 16px;
  margin-top: 24px;

  p {
     margin: 0 0 24px 0;
  }
`;

const LastRegistrationDate = Styled.p`
  display: flex;
  font-size: 14px;
  color: #243240;
  margin: 0 5px 15px 0;

  svg {
    flex-shrink: 0;
    margin-top: 3px;
    margin-right: 5px;
  }


  @media(min-width: ${({ theme }) => theme.viewports.tablet}px){
    flex-direction: row-reverse;
    margin-left: 10px;

    svg {
      margin-left: 10px;
    }
  }
`;

const Language = Styled.img`
  width: 25px;
  height: 25px;
  border-radius: 50%;
  margin-left: 5px;
`;

const EventSection = Styled.div`
  padding-bottom: 20px;
  margin-top: 20px;
  margin-bottom: 40px;

  :not(:last-of-type) {
    border-bottom: 1px solid #e0e0e0;
  }
`;

const EventDates = Styled.div``;

export const EventDateTime = Styled.p`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(1fr, 50%));
  grid-row-gap: 5px;
  line-height: 1.4;
  text-transform: capitalize;
  padding: 20px 30px;
  margin: 0 0 10px;
  background-color: #F0F5FB;
  
  & + & {
    margin-top: 15px;
  }

  span {
    display: flex;
    align-items: center;
    
    svg {
      height: 16px;
      margin-right: 8px;
    }
  }

  @media(min-width: ${({ theme }) => theme.viewports.mobile}px){
    grid-template-columns: minmax(max-content, 1fr) max-content;
  }
`;

const InstructorAvatar = Styled(Image)<ImageProps>`
  border-radius: 50%;
  
  :before {
    content: "";
    display: block;
    width: 100%;
    height: 100%;
    background-image: url(/static/profile-placeholder.svg);
    background-position: center center;
    background-size: cover;
  }
`;

const InstructorName = Styled.p`
  font-size: 18px;
  color: #363636;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
`;

const InstructorEvents = Styled(Link)`
  display: flex;
  align-items: center;
  color: #363636;
  font-weight: 600;
  text-decoration: none;

  span {
    color: #129edc;
    margin-right: 5px;
  }
  
  svg {
    margin-left: 10px;
  }
`;

const Store = Styled.p`
  font-weight: 600;
  margin: 20px 0 10px 0;

  a {
    color: #119EDD;
    text-decoration: none;
  }
`;

const PreRequisites = Styled.ul`
  display: flex;
  flex-direction: column;
  list-style: none;
  padding-left: 0;
  margin: 0;
  
  li::before {
    content: "•";
    display: inline-block;
    margin-right: 20px;
    margin-left: 10px;
  }
  li:not(:last-of-type) {
    margin-right: 50px;
  }
  
  @media(min-width: ${({ theme }) => theme.viewports.tablet}px){
    flex-direction: row;
  }
`;

const languages = {
  se: '/static/icons/se.png',
  // no: '/static/icons/no.png',
  // fi: '/static/icons/fi.png',
  en: '/static/icons/en.png',
};

const localeMap = {
  en: enGB,
  se: sv,
  no: nb,
  fi,
};

const levels = {
  beginner: <Trans id="live.event.levels.beginner" />,
  intermediate: <Trans id="live.event.levels.intermediate" />,
  advanced: <Trans id="live.event.levels.advanced" />,
  everyone: <Trans id="live.event.levels.everyone" />,
  other: <Trans id="live.event.levels.other" />,
};

const addProtocolToImage = (image: string) => {
  if (image[0] === '/' && image[1] === '/') {
    return `https:${image}`;
  }
  return image;
};

const EventPage = ({ pageProps }) => {
  const router = useRouter();
  const { slug, lang } = router.query;

  const [currentUser] = useContext(UserContext);
  const { data, loading, error } = useLiveEventQuery({
    variables: { slug: slug as string },
    ssr: false,
  });

  if (error) {
    return 'something unexpected happened!!';
  }

  const event: LiveEvent = pageProps?.eventData || data?.liveEvent;
  if (loading && !event) {
    return (
      <Flex justifyContent="center" alignItems="center" my={50}>
        <Spinner size={28} spinnerColor="#242424" />
      </Flex>
    );
  }

  // const eligableForFutureDicount =
  //   // since we do not load currentUser.receipts in server
  //   typeof window !== 'undefined' &&
  //   currentUser?.isPremium &&
  //   (['benify', 'wellness'].includes(currentUser?.receipts?.[0]?.paymentType) ||
  //     (currentUser?.subscription?.status !== SubscriptionStatus['Active'] &&
  //       !currentUser?.subscription?.inTrialPeriod));

  const userAccess = event.userAccess;

  return (
    <>
      <MetaTags
        title={`${event.title} | YOGOBE`}
        description={t({
          id: 'live.live_page.metadata.desc',
          values: {
            instructor: event.instructors?.[0]?.name,
            eventName: event.title,
          },
        })}
        thumbnailUrl={event.thumbnail}
      />
      <div style={{ backgroundColor: '#f0f5fb' }}>
        <Container
          flexDirection="column"
          alignItems="flex-start"
          px={20}
          py={30}
        >
          <AllEventsLink appearance="dark" href={`/${lang}/live`} link>
            <ArrowRightIcon />
            <Trans id="live.event_page.all_event_link" />
          </AllEventsLink>
          <EventDetails event={event} />
        </Container>
      </div>
      <EventStickyBanner
        event={event}
        userAccess={userAccess}
        premiumUser={currentUser?.isNoneTrialPremium}
      />
      <Content>
        {userAccess ? (
          <Box my={40}>
            <EventZoomDetails userAccess={userAccess} />
          </Box>
        ) : null}
        <EventSection>
          <SectionTitle>
            <Trans id="live.event_page.about_event" />
          </SectionTitle>
          {event?.isExternal ? (
            <Banner appearance="warning">
              {event?.hostedByPageMessage ? (
                event.hostedByPageMessage
              ) : (
                <Trans
                  id="live.event_page.hosted_by_banner"
                  values={{
                    host: event.hostedBy,
                  }}
                />
              )}
            </Banner>
          ) : null}
          <Flex flexDirection={['column', 'column', 'row']} mt={20}>
            <Flex alignItems="center" flex={1}>
              <Tag full>{LIVE_KINDS[event.kind]}</Tag>
              <Tag outline>{event.duration} MIN</Tag>
              <LanguageTag size={12} plain>
                <Trans id="live.event_page.event_lang_label" />{' '}
                <Language src={languages[event.language]} />
              </LanguageTag>
            </Flex>
            <Tag plain>
              <LightningIcon />
              {event.theme}
            </Tag>
            <Tag plain>
              <PulseIcon />
              {levels[event.level]}
            </Tag>
          </Flex>
          <EventDescription
            className="embeddedText"
            dangerouslySetInnerHTML={{ __html: event.descriptionLong }}
          />
        </EventSection>
        <EventSection>
          <Flex
            flexDirection={['column', 'column', 'row', 'row']}
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <SectionTitle>
              <Trans id="live.event_page.event_date_time" />
            </SectionTitle>
            <LastRegistrationDate>
              <InfoIcon />
              <Trans id="live.event_page.registration_end_label" />:{' '}
              {format(
                new Date(event.registrationEndDate),
                "dd MMMM yyyy 'at' HH:mm OOOO",
                { locale: localeMap[lang as string] }
              )}
            </LastRegistrationDate>
          </Flex>
          <EventDates>
            {event.eventDates.map((date, i) => {
              const eventDate = new Date(date);
              const fullDate = format(eventDate, 'EEEE dd MMMM yyyy', {
                locale: localeMap[lang as string],
              });

              const startTime = format(eventDate, 'H:mm');
              const endTime = format(
                addMinutes(eventDate, event.duration),
                'H:mm OOO'
              );

              return (
                <React.Fragment key={i}>
                  <EventDateTime data-testid="EventDateTime">
                    <span>
                      <CalendarIcon /> {fullDate}
                    </span>
                    <span>
                      <ClockIcon /> {startTime} - {endTime}
                    </span>
                  </EventDateTime>
                  {userAccess
                    ? event.linkToRecordings.map(recording => {
                        if (recording.eventDate === date) {
                          return (
                            <EventRecordingRow
                              event={event}
                              recording={recording}
                            />
                          );
                        }

                        return null;
                      })
                    : null}
                </React.Fragment>
              );
            })}
          </EventDates>
        </EventSection>
        <EventSection>
          <SectionTitle>
            <Trans id="live.event_page.event_instructors" />
          </SectionTitle>
          <Box mb={-15}>
            {event?.instructors?.map?.(instructor => {
              return (
                <Flex
                  key={instructor.slug}
                  alignItems={['left', 'center', 'center']}
                  flexDirection={['column', 'row', 'row']}
                  mb={15}
                  data-testid="EventInstructor"
                >
                  <InstructorAvatar
                    alt=""
                    src={addProtocolToImage(instructor?.assets?.squareImage)}
                    width={140}
                    height={140}
                  />
                  <Box ml={[0, 20, 20]}>
                    <InstructorModal slug={instructor?.slug}>
                      {openModal => (
                        <InstructorName onClick={() => openModal()}>
                          {instructor?.name}
                        </InstructorName>
                      )}
                    </InstructorModal>
                    <InstructorEvents
                      href={`/${lang}/live?instructor=${instructor?.slug}`}
                      data-testid="EventInstructor--EventsLink"
                    >
                      <Trans
                        id="live.event_page.view_all_instructor_events"
                        components={[<span key="0"></span>]}
                        values={{ name: instructor?.name }}
                      />
                      <ArrowRightIcon />
                    </InstructorEvents>
                  </Box>
                </Flex>
              );
            })}
          </Box>
        </EventSection>
        <EventSection>
          <Flex flexDirection="column">
            <SectionTitle>
              <Trans id="live.event_page.event_prerequisites" />
            </SectionTitle>
            <PreRequisites>
              {event?.accessories?.length > 0 ? (
                event.accessories.map((accesoire, i) => (
                  <li key={i}>{accesoire}</li>
                ))
              ) : (
                <Trans id="live.event_page.no_prerequisites_required" />
              )}
            </PreRequisites>
            <Store>
              <Trans
                id="live.event_page.prerequisites_yogobe_store"
                components={[
                  <a
                    key="0"
                    href="https://store.yogobe.com"
                    target="_blank"
                    rel="noreferrer"
                  ></a>,
                ]}
              />
            </Store>
          </Flex>
        </EventSection>
      </Content>
      <ReserveBanner event={event} userHasAccess={!!userAccess} />
      {!currentUser?.isNoneTrialPremium ? (
        <PricingSection id="subscription-offers" />
      ) : null}
    </>
  );
};

export const getServerSideProps = async ctx => {
  const { slug, lang } = ctx.params;
  // if(process.env.SITE_ENV === "Test")

  if (ctx.req.apolloClient) {
    const apolloClient = ctx.req.apolloClient as ApolloClient<InMemoryCache>;
    const { data } = await apolloClient.query<LiveEventQuery>({
      query: LiveEventDocument,
      variables: { slug },
    });

    if (!data.liveEvent) {
      redirect(ctx, `/${lang}/live`);
    }

    return {
      props: {
        eventData: data.liveEvent,
      },
    };
  } else {
    return {
      props: {},
    };
  }
};

export default EventPage;
